import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import {
  deleteRelatedDocument,
  downloadWorkOrderAttachment,
  getRelatedDocuments,
  getWorkOrderAttachments,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { getDocumentTypeBadge } from "../workOrderEnums";
import RelatedDocumentFormDialog from "./RelatedDocumentFormDialog";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { formatTimestamp } from "../../cmms/cmmsFormat";
import { isSafeExternalUrl } from "../../cmms/cmmsUrl";
import { CmmsListSkeleton, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette, getContainedButtonSx } from "../../cmms/assetHierarchyStyles";

export default function RelatedDocumentList({ workOrderId, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const loadFailed = intl.formatMessage(messages.relatedDocumentsLoadFailed);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const docsQuery = useQuery(
    ["cmmsWorkOrderDocuments", companyKey, workOrderId],
    () => getRelatedDocuments(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 30000 },
  );

  const attachmentsQuery = useQuery(
    ["cmmsWorkOrderAttachments", companyKey, workOrderId],
    () => getWorkOrderAttachments(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 30000 },
  );

  const attachmentById = new Map((attachmentsQuery.data ?? []).map((a) => [a.id, a]));

  const deleteMutation = useMutation({
    mutationFn: (linkId) => deleteRelatedDocument(companyKey, workOrderId, linkId),
  });

  const documents = (docsQuery.data ?? [])
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const handleSuccess = async () => {
    await docsQuery.refetch();
  };

  const handleDelete = (doc) => {
    setConfirmation({
      title: intl.formatMessage(messages.relatedDocumentsDeleteTitle),
      message: intl.formatMessage(messages.relatedDocumentsDeleteMessage, { title: doc.title }),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(doc.id);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastDocumentRemoved));
          await docsQuery.refetch();
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, loadFailed));
        }
      },
    });
  };

  const handleDownloadAttachment = async (attachmentId) => {
    const attachment = attachmentById.get(attachmentId);
    try {
      await downloadWorkOrderAttachment(companyKey, workOrderId, attachmentId, attachment?.fileName);
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, loadFailed));
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <FormattedMessage {...messages.relatedDocumentsCount} values={{ count: documents.length }} />
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)} sx={getContainedButtonSx(cmmsColors)}>
          <FormattedMessage {...messages.relatedDocumentsAdd} />
        </Button>
      </Stack>

      {docsQuery.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }
      {docsQuery.error &&
        <CmmsErrorState error={docsQuery.error} fallback={loadFailed} onRetry={() => docsQuery.refetch()} isRetrying={docsQuery.isFetching} dense />
      }
      {!docsQuery.isLoading && !docsQuery.error && documents.length === 0 &&
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          <FormattedMessage {...messages.relatedDocumentsEmpty} />
        </Typography>
      }
      {documents.length > 0 &&
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage {...messages.fieldType} /></TableCell>
              <TableCell><FormattedMessage {...messages.fieldTitle} /></TableCell>
              <TableCell><FormattedMessage {...messages.columnLink} /></TableCell>
              <TableCell><FormattedMessage {...messages.columnAdded} /></TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => {
              const badge = getDocumentTypeBadge(doc.documentType, intl);
              return (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Chip label={badge.label} size="small" sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{doc.title}</TableCell>
                  <TableCell sx={{ wordBreak: "break-all" }}>
                    {doc.externalUrl && isSafeExternalUrl(doc.externalUrl) &&
                      <Link href={doc.externalUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                        <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                          <OpenInNewIcon fontSize="inherit" />
                          <span><FormattedMessage {...messages.relatedDocumentsExternal} /></span>
                        </Stack>
                      </Link>
                    }
                    {/* Rows saved before the server rejected non-http schemes: show the value so it
                        can be recognised and deleted, but never as something clickable. */}
                    {doc.externalUrl && !isSafeExternalUrl(doc.externalUrl) &&
                      <Typography variant="body2" component="span" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        {doc.externalUrl}
                      </Typography>
                    }
                    {doc.attachmentId &&
                      <Link component="button" type="button" underline="hover" onClick={() => handleDownloadAttachment(doc.attachmentId)}>
                        {attachmentById.get(doc.attachmentId)?.fileName
                          ?? intl.formatMessage(messages.relatedDocumentsAttachmentFallback, { id: doc.attachmentId })}
                      </Link>
                    }
                  </TableCell>
                  <TableCell>{formatTimestamp(intl, doc.createdAt)}</TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label={intl.formatMessage(messages.relatedDocumentsDeleteTitle)} onClick={() => handleDelete(doc)}>
                      <DeleteOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      }

      <RelatedDocumentFormDialog
        open={formOpen}
        workOrderId={workOrderId}
        companyKey={companyKey}
        onClose={() => setFormOpen(false)}
        onSuccess={handleSuccess}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={deleteMutation.isPending || deleteMutation.isLoading} />
    </Box>
  );
}
